import { Injectable } from '@angular/core';
import { DbBaseService, ISayfaliSonuc } from '../core/db-base.service';
import { escapeRegex } from '@/src/app/uygulamalar/yuz-tanima/utils/regex.utils';
import type {
  IFirma, IFirmaYetki, FirmaRol, FirmaYetkiTuru,
  IFirmaCihaz, CihazUygulamaModu,
  IFirmaPersonel, IFirmaDepartman, IVardiya, IVardiyaGrup,
  IGirisCikis, IIzin,
} from '../interfaces';

// ─── KOLEKSIYON KOMUTLARI ─────────────────────────────────────────────────────

const DB = {
  firmalar:          (op: string) => `FikoYT.sistem.DB.hareketler.${op}`,
  firma_yetkileri:   (op: string) => `FikoYT.sistem.DB.hareketler.${op}`,
  firma_cihazlar:    (op: string) => `FikoYT.sistem.DB.hareketler.${op}`,
  firma_personeller: (op: string) => `FikoYT.sistem.DB.hareketler.${op}`,
  firma_departmanlar:(op: string) => `FikoYT.sistem.DB.hareketler.${op}`,
  firma_vardiyalar:  (op: string) => `FikoYT.sistem.DB.hareketler.${op}`,
  firma_vardiya_gruplari: (op: string) => `FikoYT.sistem.DB.hareketler.${op}`,
  firma_giris_cikis: (op: string) => `FikoYT.sistem.DB.hareketler.${op}`,
  firma_izinler:     (op: string) => `FikoYT.sistem.DB.hareketler.${op}`,
  firma_denetim:     (op: string) => `FikoYT.sistem.DB.hareketler.${op}`,
} as const;

@Injectable({ providedIn: 'root' })
export class FirmaService extends DbBaseService {

  // ═══════════════════════════════════════════════════════════════════════════
  // FİRMA CRUD
  // ═══════════════════════════════════════════════════════════════════════════

  async firmaListesi(sayfa = 1, limit = 20): Promise<ISayfaliSonuc<IFirma>> {
    const sonuc = await this.aggregate(DB.firmalar('aggregate'),
      this.sayfaliPipeline({ vt: 'FikoYT.sistem.firmalar', aktif: true }, { olusturulmaTarihi: -1 }, sayfa, limit),
    );
    return this.sayfaliSonucCoz<IFirma>(sonuc);
  }

  async firmaGetir(firmaId: string): Promise<IFirma | null> {
    const cacheKey = `firma:${firmaId}`;
    const cached = this.cached<IFirma>(cacheKey);
    if (cached) return cached;

    const sonuc = await this.aggregate<IFirma>(DB.firmalar('aggregate'), [
      { $match: { vt: 'FikoYT.sistem.firmalar' } },
      { $match: { _id: firmaId } },
      { $limit: 1 },
    ]);
    const firma = sonuc?.[0] || null;
    if (firma) this.cacheSet(cacheKey, firma);
    return firma;
  }

  async firmaEkle(firma: Partial<IFirma>): Promise<any> {
    const sonuc = await this.ekle(DB.firmalar('ekle'), {
      ...firma,
      vt: 'FikoYT.sistem.firmalar',
      aktif: true,
      olusturan: this.aktifKullaniciId,
      olusturulmaTarihi: this.simdi(),
      guncellenmeTarihi: this.simdi(),
    });
    this.cacheTemizle('firma:');
    await this.denetimKaydet(sonuc?._id || '', 'firma_olusturuldu', 'firma', { firmaAdi: firma.firmaAdi });
    return sonuc;
  }

  async firmaGuncelle(firmaId: string, veri: Partial<IFirma>): Promise<any> {
    const sonuc = await this.guncelle(DB.firmalar('guncelle'),
      { _id: firmaId },
      { $set: { ...veri, guncellenmeTarihi: this.simdi() } },
    );
    this.cacheTemizle('firma:');
    await this.denetimKaydet(firmaId, 'firma_guncellendi', 'firma', { alanlar: Object.keys(veri) });
    return sonuc;
  }

  async firmaSil(firmaId: string): Promise<any> {
    const sonuc = await this.guncelle(DB.firmalar('guncelle'),
      { _id: firmaId },
      { $set: { aktif: false, guncellenmeTarihi: this.simdi() } },
    );
    this.cacheTemizle('firma:');
    await this.denetimKaydet(firmaId, 'firma_silindi', 'firma');
    return sonuc;
  }

  async firmaKoduKontrol(firmaKodu: string, haricId?: string): Promise<boolean> {
    const match: any = { firmaKodu, aktif: true };
    if (haricId) match._id = { $ne: haricId };
    const sonuc = await this.aggregate(DB.firmalar('aggregate'), [
      { $match: { vt: 'FikoYT.sistem.firmalar' } },
      { $match: match },
      { $count: 'sayi' },
    ]);
    return (sonuc?.[0]?.sayi || 0) > 0;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // YETKİ YÖNETİMİ
  // ═══════════════════════════════════════════════════════════════════════════

  async firmaYetkiAta(
    firmaId: string,
    kullaniciId: string,
    rol: FirmaRol,
    yetkiler: FirmaYetkiTuru[],
    departmanlar?: string[],
  ): Promise<any> {
    const sonuc = await this.ekle(DB.firma_yetkileri('ekle'), {
      vt: 'FikoYT.sistem.firma.yetkiler',
      firmaId, kullaniciId, rol, yetkiler,
      departmanlar: departmanlar || [],
      olusturan: this.aktifKullaniciId,
      olusturulmaTarihi: this.simdi(),
    });
    await this.denetimKaydet(firmaId, 'yetki_atandi', 'yetki', { kullaniciId, rol });
    return sonuc;
  }

  async firmaYetkiGuncelle(yetkiId: string, firmaId: string, veri: Partial<IFirmaYetki>): Promise<any> {
    const sonuc = await this.guncelle(DB.firma_yetkileri('guncelle'),
      { _id: yetkiId },
      { $set: veri },
    );
    await this.denetimKaydet(firmaId, 'yetki_guncellendi', 'yetki', { yetkiId });
    return sonuc;
  }

  async firmaYetkiSil(yetkiId: string, firmaId: string): Promise<any> {
    const sonuc = await this.sil(DB.firma_yetkileri('sil'), { _id: yetkiId });
    await this.denetimKaydet(firmaId, 'yetki_silindi', 'yetki', { yetkiId });
    return sonuc;
  }

  async firmaYetkileri(firmaId: string): Promise<IFirmaYetki[]> {
    return this.aggregate<IFirmaYetki>(DB.firma_yetkileri('aggregate'), [
      { $match: { vt: 'FikoYT.sistem.firma.yetkiler' } },
      { $match: { firmaId } },
      {
        $lookup: {
          from: 'goUyeler',
          localField: 'kullaniciId',
          foreignField: '_id',
          as: 'kullanici',
        },
      },
      { $unwind: { path: '$kullanici', preserveNullAndEmptyArrays: true } },
      { $sort: { olusturulmaTarihi: -1 } },
    ]);
  }

  async kullaniciFirmaYetkileri(kullaniciId: string): Promise<IFirmaYetki[]> {
    return this.aggregate<IFirmaYetki>(DB.firma_yetkileri('aggregate'), [
      { $match: { vt: 'FikoYT.sistem.firma.yetkiler' } },
      { $match: { kullaniciId } },
      {
        $lookup: {
          from: 'hareketler',
          let: { fId: '$firmaId' },
          pipeline: [
            { $match: { vt: 'FikoYT.sistem.firmalar' } },
            { $match: { $expr: { $eq: ['$_id', '$$fId'] } } },
          ],
          as: 'firma',
        },
      },
      { $unwind: '$firma' },
      { $match: { 'firma.aktif': true } },
    ]);
  }

  async kullaniciFirmaYetkisiVar(kullaniciId: string, firmaId: string): Promise<IFirmaYetki | null> {
    const sonuc = await this.aggregate<IFirmaYetki>(DB.firma_yetkileri('aggregate'), [
      { $match: { vt: 'FikoYT.sistem.firma.yetkiler' } },
      { $match: { kullaniciId, firmaId } },
      { $limit: 1 },
    ]);
    return sonuc?.[0] || null;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CİHAZ YÖNETİMİ
  // ═══════════════════════════════════════════════════════════════════════════

  async cihazListesi(firmaId: string, sayfa = 1, limit = 50): Promise<ISayfaliSonuc<IFirmaCihaz>> {
    const sonuc = await this.aggregate(DB.firma_cihazlar('aggregate'),
      this.sayfaliPipeline(
        this.firmaMatch(firmaId, { vt: 'FikoYT.sistem.cihazlar', aktivasyonDurumu: { $ne: 'iptal' } }),
        { olusturulmaTarihi: -1 },
        sayfa, limit,
        [
          {
            $lookup: {
              from: 'hareketler',
              let: { dId: '$departmanId' },
              pipeline: [
                { $match: { vt: 'FikoYT.sistem.firma.departmanlar' } },
                { $match: { $expr: { $eq: ['$_id', '$$dId'] } } },
              ],
              as: 'departman',
            },
          },
          { $unwind: { path: '$departman', preserveNullAndEmptyArrays: true } },
        ],
      ),
    );
    return this.sayfaliSonucCoz<IFirmaCihaz>(sonuc);
  }

  async cihazGetir(cihazId: string): Promise<IFirmaCihaz | null> {
    const sonuc = await this.aggregate<IFirmaCihaz>(DB.firma_cihazlar('aggregate'), [
      { $match: { vt: 'FikoYT.sistem.cihazlar' } },
      { $match: { _id: cihazId } },
      {
        $lookup: {
          from: 'hareketler',
          let: { fId: '$firmaId' },
          pipeline: [
            { $match: { vt: 'FikoYT.sistem.firmalar' } },
            { $match: { $expr: { $eq: ['$_id', '$$fId'] } } },
          ],
          as: 'firma',
        },
      },
      { $unwind: { path: '$firma', preserveNullAndEmptyArrays: true } },
      { $limit: 1 },
    ]);
    return sonuc?.[0] || null;
  }

  async aktivasyonKoduOlustur(firmaId: string, cihazBilgi: Partial<IFirmaCihaz>): Promise<string> {
    // Mükerrer kayıt kontrolü: aynı firma + cihaz adı
    if (cihazBilgi.cihazAdi) {
      const mevcut = await this.aggregate(DB.firma_cihazlar('aggregate'), [
        { $match: { vt: 'FikoYT.sistem.cihazlar' } },
        { $match: { firmaId, cihazAdi: cihazBilgi.cihazAdi, aktivasyonDurumu: { $ne: 'iptal' } } },
        { $count: 'sayi' },
      ]);
      if (mevcut?.[0]?.sayi > 0) {
        throw new Error('MUKERRER_CIHAZ');
      }
    }

    const kod = this.generateAktivasyonKodu();
    await this.ekle(DB.firma_cihazlar('ekle'), {
      ...cihazBilgi,
      vt: 'FikoYT.sistem.cihazlar',
      firmaId,
      aktivasyonKodu: kod,
      aktivasyonDurumu: 'bekliyor',
      cevrimiciMi: false,
      konfigurasyon: cihazBilgi.konfigurasyon || {},
      olusturulmaTarihi: this.simdi(),
    });
    await this.denetimKaydet(firmaId, 'aktivasyon_kodu_olusturuldu', 'cihaz', { kod, cihazAdi: cihazBilgi.cihazAdi });
    return kod;
  }

  async cihazAktivasyonDogrula(kod: string, cihazKimlik: string): Promise<IFirmaCihaz | null> {
    const sonuc = await this.aggregate<IFirmaCihaz>(DB.firma_cihazlar('aggregate'), [
      { $match: { vt: 'FikoYT.sistem.cihazlar' } },
      { $match: { aktivasyonKodu: kod, aktivasyonDurumu: 'bekliyor' } },
      {
        $lookup: {
          from: 'hareketler',
          let: { fId: '$firmaId' },
          pipeline: [
            { $match: { vt: 'FikoYT.sistem.firmalar' } },
            { $match: { $expr: { $eq: ['$_id', '$$fId'] } } },
          ],
          as: 'firma',
        },
      },
      { $unwind: '$firma' },
      { $limit: 1 },
    ]);

    if (!sonuc?.length) return null;

    await this.guncelle(DB.firma_cihazlar('guncelle'),
      { _id: sonuc[0]._id },
      {
        $set: {
          cihazKimlik,
          aktivasyonDurumu: 'aktif',
          aktivasyonTarihi: this.simdi(),
        },
      },
    );

    await this.denetimKaydet(sonuc[0].firmaId, 'cihaz_aktiflestirildi', 'cihaz', { cihazKimlik, kod });
    return sonuc[0];
  }

  async cihazGuncelle(cihazId: string, firmaId: string, veri: Partial<IFirmaCihaz>): Promise<any> {
    const sonuc = await this.guncelle(DB.firma_cihazlar('guncelle'),
      { _id: cihazId },
      { $set: { ...veri, guncellenmeTarihi: this.simdi() } },
    );
    await this.denetimKaydet(firmaId, 'cihaz_guncellendi', 'cihaz', { cihazId });
    return sonuc;
  }

  async cihazDurumGuncelle(cihazId: string, firmaId: string, durum: 'aktif' | 'askida' | 'iptal'): Promise<any> {
    const sonuc = await this.guncelle(DB.firma_cihazlar('guncelle'),
      { _id: cihazId },
      { $set: { aktivasyonDurumu: durum, guncellenmeTarihi: this.simdi() } },
    );
    await this.denetimKaydet(firmaId, 'cihaz_durum_degisti', 'cihaz', { cihazId, durum });
    return sonuc;
  }

  private generateAktivasyonKodu(): string {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Karışabilecek karakterler çıkarıldı (0/O, 1/I)
    let kod = '';
    for (let i = 0; i < 6; i++) {
      kod += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return kod;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PERSONEL YÖNETİMİ
  // ═══════════════════════════════════════════════════════════════════════════

  async personelListesi(
    firmaId: string,
    sayfa = 1,
    limit = 20,
    filtre?: { departmanId?: string; arama?: string; aktif?: boolean },
  ): Promise<ISayfaliSonuc<IFirmaPersonel>> {
    const match: any = this.firmaMatch(firmaId, { aktif: filtre?.aktif !== false });
    if (filtre?.departmanId) match.departmanId = filtre.departmanId;
    if (filtre?.arama) {
      const regex = { $regex: escapeRegex(filtre.arama), $options: 'i' };
      match.$or = [
        { ad: regex },
        { soyad: regex },
        { sicilNo: regex },
        { kartUid: regex },
      ];
    }

    match.vt = 'FikoYT.sistem.firma.personeller';
    const sonuc = await this.aggregate(DB.firma_personeller('aggregate'),
      this.sayfaliPipeline(match, { ad: 1, soyad: 1 }, sayfa, limit, [
        {
          $lookup: {
            from: 'hareketler',
            let: { dId: '$departmanId' },
            pipeline: [
              { $match: { vt: 'FikoYT.sistem.firma.departmanlar' } },
              { $match: { $expr: { $eq: ['$_id', '$$dId'] } } },
            ],
            as: 'departman',
          },
        },
        { $unwind: { path: '$departman', preserveNullAndEmptyArrays: true } },
      ]),
    );
    return this.sayfaliSonucCoz<IFirmaPersonel>(sonuc);
  }

  async personelGetir(personelId: string): Promise<IFirmaPersonel | null> {
    const sonuc = await this.aggregate<IFirmaPersonel>(DB.firma_personeller('aggregate'), [
      { $match: { vt: 'FikoYT.sistem.firma.personeller' } },
      { $match: { _id: personelId } },
      {
        $lookup: {
          from: 'hareketler',
          let: { dId: '$departmanId' },
          pipeline: [
            { $match: { vt: 'FikoYT.sistem.firma.departmanlar' } },
            { $match: { $expr: { $eq: ['$_id', '$$dId'] } } },
          ],
          as: 'departman',
        },
      },
      { $unwind: { path: '$departman', preserveNullAndEmptyArrays: true } },
      { $limit: 1 },
    ]);
    return sonuc?.[0] || null;
  }

  async personelEkle(firmaId: string, veri: Partial<IFirmaPersonel>): Promise<any> {
    const sonuc = await this.ekle(DB.firma_personeller('ekle'), {
      ...veri,
      vt: 'FikoYT.sistem.firma.personeller',
      firmaId,
      aktif: true,
      izinHakki: veri.izinHakki || { yillikIzin: 14, kullanilanIzin: 0, kalanIzin: 14 },
      olusturulmaTarihi: this.simdi(),
      guncellenmeTarihi: this.simdi(),
    });
    this.cacheTemizle(`personel:${firmaId}`);
    await this.denetimKaydet(firmaId, 'personel_eklendi', 'personel', { ad: veri.ad, soyad: veri.soyad });
    return sonuc;
  }

  async personelGuncelle(personelId: string, firmaId: string, veri: Partial<IFirmaPersonel>): Promise<any> {
    const sonuc = await this.guncelle(DB.firma_personeller('guncelle'),
      { _id: personelId },
      { $set: { ...veri, guncellenmeTarihi: this.simdi() } },
    );
    this.cacheTemizle(`personel:${firmaId}`);
    await this.denetimKaydet(firmaId, 'personel_guncellendi', 'personel', { personelId });
    return sonuc;
  }

  async personelSil(personelId: string, firmaId: string): Promise<any> {
    const sonuc = await this.guncelle(DB.firma_personeller('guncelle'),
      { _id: personelId },
      { $set: { aktif: false, istenCikisTarihi: this.simdi(), guncellenmeTarihi: this.simdi() } },
    );
    this.cacheTemizle(`personel:${firmaId}`);
    await this.denetimKaydet(firmaId, 'personel_silindi', 'personel', { personelId });
    return sonuc;
  }

  async kartAtama(personelId: string, firmaId: string, kartUid: string): Promise<any> {
    const sonuc = await this.guncelle(DB.firma_personeller('guncelle'),
      { _id: personelId },
      { $set: { kartUid, kartAtanmaTarihi: this.simdi(), guncellenmeTarihi: this.simdi() } },
    );
    await this.denetimKaydet(firmaId, 'kart_atandi', 'personel', { personelId, kartUid });
    return sonuc;
  }

  async kartIptal(personelId: string, firmaId: string): Promise<any> {
    const sonuc = await this.guncelle(DB.firma_personeller('guncelle'),
      { _id: personelId },
      { $set: { guncellenmeTarihi: this.simdi() }, $unset: { kartUid: '', kartAtanmaTarihi: '' } },
    );
    await this.denetimKaydet(firmaId, 'kart_iptal_edildi', 'personel', { personelId });
    return sonuc;
  }

  async kartUidKontrol(firmaId: string, kartUid: string, haricId?: string): Promise<boolean> {
    const match: any = this.firmaMatch(firmaId, { kartUid, aktif: true });
    if (haricId) match._id = { $ne: haricId };
    const sonuc = await this.aggregate(DB.firma_personeller('aggregate'), [
      { $match: { vt: 'FikoYT.sistem.firma.personeller' } },
      { $match: match },
      { $count: 'sayi' },
    ]);
    return (sonuc?.[0]?.sayi || 0) > 0;
  }

  /**
   * HMI kart okutulduğunda firma personelinden kartUid ile sorgula.
   * Tek pipeline ile departman + üye profili birleştirilir (2 istek yerine 1).
   * 60s TTL cache ile aynı kart tekrar okutulduğunda anlık cevap.
   */
  private _kartCache = new Map<string, { veri: IFirmaPersonel; zaman: number }>();
  private readonly _KART_CACHE_TTL = 60_000; // 60 saniye

  async kartSorgula(firmaId: string, kartUid: string): Promise<IFirmaPersonel | null> {
    // Cache kontrolü
    const cacheKey = `${firmaId}:${kartUid}`;
    const cached = this._kartCache.get(cacheKey);
    if (cached && (Date.now() - cached.zaman) < this._KART_CACHE_TTL) {
      return cached.veri;
    }

    const match: any = firmaId
      ? this.firmaMatch(firmaId, { kartUid, aktif: true })
      : { kartUid, aktif: true };

    // Tek pipeline: personel + departman + üye profili ($lookup ile birleştir)
    const sonuc = await this.aggregate(DB.firma_personeller('aggregate'), [
      { $match: { vt: 'FikoYT.sistem.firma.personeller' } },
      { $match: match },
      { $limit: 1 },
      {
        $lookup: {
          from: 'hareketler',
          let: { dId: '$departmanId' },
          pipeline: [
            { $match: { vt: 'FikoYT.sistem.firma.departmanlar' } },
            { $match: { $expr: { $eq: ['$_id', '$$dId'] } } },
          ],
          as: '_dept',
        },
      },
      { $addFields: { departman: { $arrayElemAt: ['$_dept', 0] } } },
      {
        $lookup: {
          from: 'goUyeler',
          localField: 'kullaniciId',
          foreignField: '_id',
          as: '_uye',
        },
      },
      {
        $addFields: {
          _uyeData: { $arrayElemAt: ['$_uye', 0] },
        },
      },
      {
        $addFields: {
          _uyeIsim: {
            $ifNull: [
              '$_uyeData.isim',
              { $arrayElemAt: [{ $split: [{ $ifNull: ['$_uyeData.veri.pilesmi', ''] }, ' '] }, 0] },
            ],
          },
          _uyeSoyisim: { $ifNull: ['$_uyeData.soyIsim', ''] },
          _uyeResimId: { $ifNull: ['$_uyeData.profilResmi', { $ifNull: ['$_uyeData.veri.resimId', ''] }] },
          _uyeKullaniciAdi: { $ifNull: ['$_uyeData.kullaniciAdi', ''] },
          _uyeYetkiler: { $ifNull: ['$_uyeData.yetkiler', { $ifNull: ['$_uyeData.veri.yetkiler', []] }] },
        },
      },
      { $project: { _dept: 0, _uye: 0, _uyeData: 0 } },
    ]);

    const personel = sonuc?.[0] || null;

    // Cache'e kaydet (null sonuçları kısa süreli cache'le — 5sn)
    if (personel) {
      this._kartCache.set(cacheKey, { veri: personel, zaman: Date.now() });
    } else {
      this._kartCache.set(cacheKey, { veri: null as any, zaman: Date.now() - this._KART_CACHE_TTL + 5000 });
    }

    return personel;
  }

  /** Test amaçlı: ad/soyad ile personel sorgula (kartSorgula ile aynı pipeline) */
  async personelAraIsimle(ad: string, soyad: string): Promise<IFirmaPersonel | null> {
    const match: any = { aktif: true, ad: { $regex: escapeRegex(ad), $options: 'i' }, soyad: { $regex: escapeRegex(soyad), $options: 'i' } };
    const sonuc = await this.aggregate(DB.firma_personeller('aggregate'), [
      { $match: { vt: 'FikoYT.sistem.firma.personeller' } },
      { $match: match },
      { $limit: 1 },
      { $lookup: { from: 'hareketler', let: { dId: '$departmanId' }, pipeline: [{ $match: { vt: 'FikoYT.sistem.firma.departmanlar' } }, { $match: { $expr: { $eq: ['$_id', '$$dId'] } } }], as: '_dept' } },
      { $addFields: { departman: { $arrayElemAt: ['$_dept', 0] } } },
      { $lookup: { from: 'goUyeler', localField: 'kullaniciId', foreignField: '_id', as: '_uye' } },
      { $addFields: { _uyeData: { $arrayElemAt: ['$_uye', 0] } } },
      {
        $addFields: {
          _uyeIsim: { $ifNull: ['$_uyeData.isim', { $arrayElemAt: [{ $split: [{ $ifNull: ['$_uyeData.veri.pilesmi', ''] }, ' '] }, 0] }] },
          _uyeSoyisim: { $ifNull: ['$_uyeData.soyIsim', ''] },
          _uyeResimId: { $ifNull: ['$_uyeData.profilResmi', { $ifNull: ['$_uyeData.veri.resimId', ''] }] },
          _uyeKullaniciAdi: { $ifNull: ['$_uyeData.kullaniciAdi', ''] },
          _uyeYetkiler: { $ifNull: ['$_uyeData.yetkiler', { $ifNull: ['$_uyeData.veri.yetkiler', []] }] },
        },
      },
      { $project: { _dept: 0, _uye: 0, _uyeData: 0 } },
    ]);
    return sonuc?.[0] || null;
  }

  async uyeAra(arama: string, limit = 10): Promise<any[]> {
    const regex = { $regex: escapeRegex(arama), $options: 'i' };
    return this.aggregate('FikoYT.sistem.DB.goUyeler.aggregate', [
      {
        $match: {
          silindi: { $ne: true },
          $or: [
            { kullaniciAdi: regex },
            { 'veri.pilesmi': regex },
            { 'veri.ePosta': regex },
          ],
        },
      },
      { $limit: limit },
      {
        $project: {
          kullaniciAdi: 1,
          'veri.pilesmi': 1,
          'veri.ePosta': 1,
          'veri.resimId': 1,
          'veri.cinsiyet': 1,
        },
      },
    ]);
  }

  async uyePersonelKontrol(kullaniciId: string, firmaId: string): Promise<boolean> {
    const sonuc = await this.aggregate(DB.firma_personeller('aggregate'), [
      { $match: { vt: 'FikoYT.sistem.firma.personeller' } },
      { $match: { firmaId, kullaniciId, aktif: true } },
      { $count: 'sayi' },
    ]);
    return (sonuc?.[0]?.sayi || 0) > 0;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // DEPARTMAN YÖNETİMİ
  // ═══════════════════════════════════════════════════════════════════════════

  async departmanListesi(firmaId: string): Promise<IFirmaDepartman[]> {
    const cacheKey = `dept:${firmaId}`;
    const cached = this.cached<IFirmaDepartman[]>(cacheKey);
    if (cached) return cached;

    const sonuc = await this.aggregate<IFirmaDepartman>(DB.firma_departmanlar('aggregate'), [
      { $match: { vt: 'FikoYT.sistem.firma.departmanlar' } },
      { $match: this.firmaMatch(firmaId, { aktif: true }) },
      { $sort: { sira: 1, departmanAdi: 1 } },
    ]);
    return this.cacheSet(cacheKey, sonuc);
  }

  async departmanListesiAgac(firmaId: string): Promise<IFirmaDepartman[]> {
    return this.aggregate<IFirmaDepartman>(DB.firma_departmanlar('aggregate'), [
      { $match: { vt: 'FikoYT.sistem.firma.departmanlar' } },
      { $match: { firmaId, aktif: true } },
      {
        $lookup: {
          from: 'hareketler',
          let: { deptId: '$_id' },
          pipeline: [
            { $match: { vt: 'FikoYT.sistem.firma.personeller' } },
            { $match: { $expr: { $eq: ['$departmanId', '$$deptId'] }, aktif: true } },
            { $count: 'sayi' },
          ],
          as: 'personelSayim',
        },
      },
      {
        $addFields: {
          personelSayisi: { $ifNull: [{ $arrayElemAt: ['$personelSayim.sayi', 0] }, 0] },
        },
      },
      { $project: { personelSayim: 0 } },
      { $sort: { sira: 1, departmanAdi: 1 } },
    ]);
  }

  async departmanEkle(firmaId: string, veri: Partial<IFirmaDepartman>): Promise<any> {
    const sonuc = await this.ekle(DB.firma_departmanlar('ekle'), {
      ...veri,
      vt: 'FikoYT.sistem.firma.departmanlar',
      firmaId,
      aktif: true,
    });
    this.cacheTemizle(`dept:${firmaId}`);
    await this.denetimKaydet(firmaId, 'departman_eklendi', 'departman', { departmanAdi: veri.departmanAdi });
    return sonuc;
  }

  async departmanGuncelle(departmanId: string, firmaId: string, veri: Partial<IFirmaDepartman>): Promise<any> {
    const sonuc = await this.guncelle(DB.firma_departmanlar('guncelle'),
      { _id: departmanId },
      { $set: veri },
    );
    this.cacheTemizle(`dept:${firmaId}`);
    return sonuc;
  }

  async departmanSil(departmanId: string, firmaId: string): Promise<any> {
    const sonuc = await this.guncelle(DB.firma_departmanlar('guncelle'),
      { _id: departmanId },
      { $set: { aktif: false } },
    );
    this.cacheTemizle(`dept:${firmaId}`);
    await this.denetimKaydet(firmaId, 'departman_silindi', 'departman', { departmanId });
    return sonuc;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // VARDİYA YÖNETİMİ
  // ═══════════════════════════════════════════════════════════════════════════

  async vardiyaListesi(firmaId: string): Promise<IVardiya[]> {
    return this.aggregate<IVardiya>(DB.firma_vardiyalar('aggregate'), [
      { $match: { vt: 'FikoYT.sistem.firma.vardiyalar' } },
      { $match: this.firmaMatch(firmaId) },
      { $sort: { baslangicSaati: 1 } },
    ]);
  }

  async vardiyaEkle(firmaId: string, veri: Partial<IVardiya>): Promise<any> {
    return this.ekle(DB.firma_vardiyalar('ekle'), { ...veri, vt: 'FikoYT.sistem.firma.vardiyalar', firmaId });
  }

  async vardiyaGuncelle(vardiyaId: string, veri: Partial<IVardiya>): Promise<any> {
    return this.guncelle(DB.firma_vardiyalar('guncelle'), { _id: vardiyaId }, { $set: veri });
  }

  async vardiyaGrupListesi(firmaId: string): Promise<IVardiyaGrup[]> {
    return this.aggregate<IVardiyaGrup>(DB.firma_vardiya_gruplari('aggregate'), [
      { $match: { vt: 'FikoYT.sistem.firma.vardiya_gruplari' } },
      { $match: this.firmaMatch(firmaId) },
      { $sort: { grupAdi: 1 } },
    ]);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // GİRİŞ/ÇIKIŞ (PDKS)
  // ═══════════════════════════════════════════════════════════════════════════

  async girisCikisKaydet(firmaId: string, kayit: Partial<IGirisCikis>): Promise<any> {
    if (kayit.istemciKayitId) {
      const mevcut = await this.aggregate<IGirisCikis>(DB.firma_giris_cikis('aggregate'), [
        { $match: { vt: 'FikoYT.sistem.giris_cikis', firmaId, istemciKayitId: kayit.istemciKayitId } },
        { $limit: 1 },
      ]);
      if (mevcut?.[0]) return mevcut[0];
    }
    return this.ekle(DB.firma_giris_cikis('ekle'), {
      ...kayit,
      vt: 'FikoYT.sistem.giris_cikis',
      firmaId,
      zaman: kayit.zaman || this.simdi(),
      onayDurumu: kayit.onayDurumu || 'otomatik',
    });
  }

  async gunlukPuantaj(firmaId: string, tarih: string): Promise<any[]> {
    const gun = new Date(tarih);
    gun.setHours(0, 0, 0, 0);
    const ertesiGun = new Date(gun);
    ertesiGun.setDate(ertesiGun.getDate() + 1);

    return this.aggregate(DB.firma_giris_cikis('aggregate'), [
      { $match: { vt: 'FikoYT.sistem.giris_cikis' } },
      { $match: { firmaId, zaman: { $gte: gun.toISOString(), $lt: ertesiGun.toISOString() } } },
      { $sort: { zaman: 1 } },
      {
        $group: {
          _id: '$personelId',
          hareketler: { $push: { tur: '$tur', zaman: '$zaman', cihazId: '$cihazId' } },
        },
      },
      {
        $lookup: {
          from: 'hareketler',
          let: { pId: '$_id' },
          pipeline: [
            { $match: { vt: 'FikoYT.sistem.firma.personeller' } },
            { $match: { $expr: { $eq: ['$_id', '$$pId'] } } },
          ],
          as: 'personel',
        },
      },
      { $unwind: '$personel' },
    ]);
  }

  async sonGirisCikis(firmaId: string, limit = 20): Promise<IGirisCikis[]> {
    return this.aggregate<IGirisCikis>(DB.firma_giris_cikis('aggregate'), [
      { $match: { vt: 'FikoYT.sistem.giris_cikis' } },
      { $match: this.firmaMatch(firmaId) },
      { $sort: { zaman: -1 } },
      { $limit: limit },
      {
        $lookup: {
          from: 'hareketler',
          let: { pId: '$personelId' },
          pipeline: [
            { $match: { vt: 'FikoYT.sistem.firma.personeller' } },
            { $match: { $expr: { $eq: ['$_id', '$$pId'] } } },
          ],
          as: 'personel',
        },
      },
      { $unwind: { path: '$personel', preserveNullAndEmptyArrays: true } },
    ]);
  }

  /**
   * HMI mola başlat/bitir kaydı.
   * firma_giris_cikis koleksiyonuna tur='mola_baslangic' veya 'mola_bitis' olarak yazar.
   */
  async molaKaydet(
    firmaId: string,
    personelId: string,
    kartUid: string,
    cihazId: string,
    tur: 'mola_baslangic' | 'mola_bitis',
  ): Promise<any> {
    return this.ekle(DB.firma_giris_cikis('ekle'), {
      vt: 'FikoYT.sistem.giris_cikis',
      firmaId,
      personelId,
      kartUid,
      cihazId,
      tur,
      zaman: this.simdi(),
      onayDurumu: 'otomatik',
    });
  }

  /**
   * Süper yetkili HMI puantaj düzeltmesi.
   * Mevcut kayıtın zamanını değiştirir ve onayDurumu='duzeltme' olarak işaretler.
   */
  async puantajDuzenle(
    firmaId: string,
    personelId: string,
    tur: 'giris' | 'cikis' | 'mola_baslangic' | 'mola_bitis',
    eskiZaman: string,
    yeniZaman: string,
    notlar: string,
  ): Promise<any> {
    const sonuc = await this.guncelle(
      DB.firma_giris_cikis('guncelle'),
      { vt: 'FikoYT.sistem.giris_cikis', firmaId, personelId, tur, zaman: eskiZaman },
      { $set: { zaman: yeniZaman, onayDurumu: 'duzeltme' as const, notlar } },
    );
    await this.denetimKaydet(firmaId, 'puantaj_duzeltildi', 'pdks', { personelId, tur, eskiZaman, yeniZaman });
    return sonuc;
  }

  async kartIlePersonelBul(firmaId: string, kartUid: string): Promise<IFirmaPersonel | null> {
    const sonuc = await this.aggregate<IFirmaPersonel>(DB.firma_personeller('aggregate'), [
      { $match: { vt: 'FikoYT.sistem.firma.personeller' } },
      { $match: this.firmaMatch(firmaId, { kartUid, aktif: true }) },
      { $limit: 1 },
    ]);
    return sonuc?.[0] || null;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // İZİN YÖNETİMİ
  // ═══════════════════════════════════════════════════════════════════════════

  async izinListesi(
    firmaId: string,
    sayfa = 1,
    limit = 20,
    filtre?: { durum?: string; personelId?: string },
  ): Promise<ISayfaliSonuc<IIzin>> {
    const match: any = this.firmaMatch(firmaId);
    if (filtre?.durum) match.durum = filtre.durum;
    if (filtre?.personelId) match.personelId = filtre.personelId;

    match.vt = 'FikoYT.sistem.firma.izinler';
    const sonuc = await this.aggregate(DB.firma_izinler('aggregate'),
      this.sayfaliPipeline(match, { olusturulmaTarihi: -1 }, sayfa, limit, [
        {
          $lookup: {
            from: 'hareketler',
            let: { pId: '$personelId' },
            pipeline: [
              { $match: { vt: 'FikoYT.sistem.firma.personeller' } },
              { $match: { $expr: { $eq: ['$_id', '$$pId'] } } },
            ],
            as: 'personel',
          },
        },
        { $unwind: { path: '$personel', preserveNullAndEmptyArrays: true } },
      ]),
    );
    return this.sayfaliSonucCoz<IIzin>(sonuc);
  }

  async izinEkle(firmaId: string, veri: Partial<IIzin>): Promise<any> {
    return this.ekle(DB.firma_izinler('ekle'), {
      ...veri,
      vt: 'FikoYT.sistem.firma.izinler',
      firmaId,
      durum: 'bekliyor',
      olusturulmaTarihi: this.simdi(),
    });
  }

  async izinDurumGuncelle(izinId: string, firmaId: string, durum: string, onaylayanId?: string): Promise<any> {
    const set: any = { durum };
    if (onaylayanId) set.onaylayanId = onaylayanId;
    const sonuc = await this.guncelle(DB.firma_izinler('guncelle'),
      { _id: izinId },
      { $set: set },
    );
    await this.denetimKaydet(firmaId, 'izin_durum_degisti', 'izin', { izinId, durum });
    return sonuc;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // FİRMA ÖZET / DASHBOARD
  // ═══════════════════════════════════════════════════════════════════════════

  async firmaOzet(firmaId: string): Promise<{
    personelSayisi: number;
    cihazSayisi: number;
    bugunGiris: number;
    aktifVardiya: number;
    bekleyenIzin: number;
  }> {
    const [personel, cihaz, bugunGiris, vardiya, izin] = await Promise.all([
      this.aggregate(DB.firma_personeller('aggregate'), [
        { $match: { vt: 'FikoYT.sistem.firma.personeller' } },
        { $match: this.firmaMatch(firmaId, { aktif: true }) },
        { $count: 'sayi' },
      ]),
      this.aggregate(DB.firma_cihazlar('aggregate'), [
        { $match: this.firmaMatch(firmaId, { vt: 'FikoYT.sistem.cihazlar', aktivasyonDurumu: 'aktif' }) },
        { $count: 'sayi' },
      ]),
      this.aggregate(DB.firma_giris_cikis('aggregate'), [
        { $match: { vt: 'FikoYT.sistem.giris_cikis' } },
        { $match: this.firmaMatch(firmaId, { tur: 'giris', zaman: { $gte: this.bugunBaslangic() } }) },
        { $count: 'sayi' },
      ]),
      this.aggregate(DB.firma_vardiyalar('aggregate'), [
        { $match: { vt: 'FikoYT.sistem.firma.vardiyalar' } },
        { $match: this.firmaMatch(firmaId, { aktif: true }) },
        { $count: 'sayi' },
      ]),
      this.aggregate(DB.firma_izinler('aggregate'), [
        { $match: { vt: 'FikoYT.sistem.firma.izinler' } },
        { $match: this.firmaMatch(firmaId, { durum: 'bekliyor' }) },
        { $count: 'sayi' },
      ]),
    ]);

    return {
      personelSayisi: personel[0]?.sayi || 0,
      cihazSayisi: cihaz[0]?.sayi || 0,
      bugunGiris: bugunGiris[0]?.sayi || 0,
      aktifVardiya: vardiya[0]?.sayi || 0,
      bekleyenIzin: izin[0]?.sayi || 0,
    };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PDKS AYARLARI
  // ═══════════════════════════════════════════════════════════════════════════

  async pdksAyarlarGetir(firmaId: string): Promise<any> {
    const sonuc = await this.aggregate('FikoYT.sistem.DB.hareketler.aggregate', [
      { $match: { vt: 'FikoYT.sistem.pdks_ayarlar' } },
      { $match: { firmaId } },
      { $limit: 1 },
    ]);
    return sonuc?.[0] || null;
  }

  async pdksAyarlarKaydet(firmaId: string, ayarlar: any): Promise<any> {
    // Upsert: varsa güncelle, yoksa ekle
    const mevcut = await this.pdksAyarlarGetir(firmaId);
    if (mevcut) {
      return this.guncelle('FikoYT.sistem.DB.hareketler.guncelle',
        { vt: 'FikoYT.sistem.pdks_ayarlar', firmaId },
        { $set: { ...ayarlar, guncellenmeTarihi: this.simdi() } },
      );
    }
    return this.ekle('FikoYT.sistem.DB.hareketler.ekle', {
      ...ayarlar,
      vt: 'FikoYT.sistem.pdks_ayarlar',
      firmaId,
      olusturulmaTarihi: this.simdi(),
      guncellenmeTarihi: this.simdi(),
    });
  }

  async aylikPuantaj(firmaId: string, yil: number, ay: number): Promise<any[]> {
    const ayBas = new Date(yil, ay, 1);
    const aySon = new Date(yil, ay + 1, 1);
    return this.aggregate(DB.firma_giris_cikis('aggregate'), [
      { $match: { vt: 'FikoYT.sistem.giris_cikis' } },
      { $match: { firmaId, zaman: { $gte: ayBas.toISOString(), $lt: aySon.toISOString() } } },
      { $sort: { zaman: 1 } },
      {
        $group: {
          _id: {
            personelId: '$personelId',
            gun: { $dateToString: { format: '%Y-%m-%d', date: { $toDate: '$zaman' } } },
          },
          hareketler: { $push: { tur: '$tur', zaman: '$zaman', cihazId: '$cihazId' } },
        },
      },
      {
        $group: {
          _id: '$_id.personelId',
          gunler: { $push: { tarih: '$_id.gun', hareketler: '$hareketler' } },
        },
      },
      {
        $lookup: {
          from: 'hareketler',
          let: { pId: '$_id' },
          pipeline: [
            { $match: { vt: 'FikoYT.sistem.firma.personeller' } },
            { $match: { $expr: { $eq: ['$_id', '$$pId'] } } },
          ],
          as: 'personel',
        },
      },
      { $unwind: '$personel' },
      { $sort: { 'personel.ad': 1 } },
    ]);
  }

  async gunlukDurumOzeti(firmaId: string): Promise<any[]> {
    const bugun = new Date();
    bugun.setHours(0, 0, 0, 0);
    const yarin = new Date(bugun);
    yarin.setDate(yarin.getDate() + 1);

    return this.aggregate(DB.firma_giris_cikis('aggregate'), [
      { $match: { vt: 'FikoYT.sistem.giris_cikis' } },
      { $match: { firmaId, zaman: { $gte: bugun.toISOString(), $lt: yarin.toISOString() } } },
      { $sort: { zaman: 1 } },
      {
        $group: {
          _id: '$personelId',
          hareketler: { $push: { tur: '$tur', zaman: '$zaman' } },
          ilkGiris: { $first: { $cond: [{ $eq: ['$tur', 'giris'] }, '$zaman', null] } },
          sonCikis: { $last: { $cond: [{ $eq: ['$tur', 'cikis'] }, '$zaman', null] } },
        },
      },
      {
        $lookup: {
          from: 'hareketler',
          let: { pId: '$_id' },
          pipeline: [
            { $match: { vt: 'FikoYT.sistem.firma.personeller' } },
            { $match: { $expr: { $eq: ['$_id', '$$pId'] } } },
          ],
          as: 'personel',
        },
      },
      { $unwind: '$personel' },
      { $match: { 'personel.aktif': true } },
      { $sort: { 'personel.ad': 1 } },
    ]);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // DENETİM KAYITLARI
  // ═══════════════════════════════════════════════════════════════════════════

  async denetimKayitlari(firmaId: string, sayfa = 1, limit = 50): Promise<ISayfaliSonuc<any>> {
    const sonuc = await this.aggregate(DB.firma_denetim('aggregate'),
      this.sayfaliPipeline({ vt: 'FikoYT.sistem.denetim', ...this.firmaMatch(firmaId) }, { tarih: -1 }, sayfa, limit),
    );
    return this.sayfaliSonucCoz(sonuc);
  }
}
